import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { Select } from "./Controls/Select";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import { useEffect, useState } from "react";
import { InviteStaffReq } from "./Data/ApiTypes";
import { StaffRole } from "./Data/ApiTransport";
import { app_roles, RoleList, requiresFacilityAssignment } from "./helpers/RoleManager";
import { FacilityApi, ServerTransferMonitorStaff } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";
import { Api } from "./Data/Api";
import Multiselect from 'react-select';

interface OperatorStaffModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    onSave: (data: any) => void;
    initialData?: ServerTransferMonitorStaff; // For edit mode
    mode: 'invite' | 'edit';
}

export interface StaffFormData {
    selectedRole: string;
    name?: string | null;
    emailAddress?: string | null;
    phoneNumber?: string | null;
    note?: string | null;
    staffId?: number;
    operatorId?: number;
    role?: any;
    roles?: string[];
    facilities?: any[];
    password?: string;
    createdWhen?: any;
    removedWhen?: any;
}

export default function OperatorStaffModal({ show, setShow, onSave, initialData, mode }: OperatorStaffModalProps) {
    const roles = app_roles.filter(c => c.identity != RoleList.SuperAdmin.identity);
    const { register, reset, formState: { errors }, handleSubmit } = useForm<StaffFormData>();
    
    const [facilities, setFacilities] = useState<any>([]);
    const [showFacilities, setShowFacilities] = useState(false);
    const [selectedValues, setSelectedValues] = useState<any>([]);
    const facilitiesApi = createApiService(FacilityApi);

    const resetData = (data?: ServerTransferMonitorStaff) => {
        if (mode === 'edit' && data) {
            // Edit mode - populate with existing data
            const firstRole = data?.roles?.[0] ?? null;
            const staffEdit = {
                createdWhen: data?.createdWhen,
                emailAddress: data?.emailAddress,
                facilities: data?.facilities,
                name: data?.name,
                note: data?.note,
                operatorId: data?.operatorId,
                phoneNumber: data?.phoneNumber,
                removedWhen: data?.removedWhen,
                role: data?.role,
                roles: data?.roles,
                selectedRole: firstRole || "",
                staffId: data?.staffId
            };
            reset(staffEdit as any);
            // Convert facilities to multiselect format for display
            const facilityOptions = data?.facilities?.map(c => ({
                label: c.name,
                value: c.facilityId
            })) || [];
            setSelectedValues(facilityOptions);
            setShowFacilities(requiresFacilityAssignment(firstRole?.toUpperCase()));
        } else {
            // Invite mode - start with empty form
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                password: "",
                note: "",
                role: StaffRole.OperatorAdmin,
                selectedRole: roles[0]?.identity || ""
            });
            setSelectedValues([]);
            setShowFacilities(requiresFacilityAssignment(roles[0]?.identity));
        }
    };

    useEffect(() => {
        if (show) {
            facilitiesApi.listFacilitiesLite().then(result => {
                const opts = (result.data.items || []).map(c => ({
                    label: c.name,
                    value: c.facilityId
                }));
                setFacilities(opts);
            });
            resetData(initialData);
        }
    }, [show, initialData, mode]);

    function selectedFacilityChange(selectedOptions: any) {
        setSelectedValues(selectedOptions || []);
    }

    function selectedRoleChange(e: any) {
        const selectedOption = e.target.selectedOptions[0];
        const selectedRole = roles.find(role => role.identity === selectedOption.text.toUpperCase());
        setShowFacilities(requiresFacilityAssignment(selectedRole?.identity));
    }

    const selectAll = () => {
        setSelectedValues(facilities);
    };

    async function submit(data: StaffFormData) {
        // Convert selected values to API format
        const facilitiesForApi = selectedValues.map((option: any) => ({ facilityId: option.value }));
        
        // Prepare data with facilities and roles
        const submitData = {
            ...data,
            roles: [data.selectedRole],
            facilities: facilitiesForApi
        };

        onSave(submitData);
        setShow(false);      
    }

    const modalTitle = mode === 'invite' ? 'Invite Staff Member' : 'Edit Staff Member';

    return (
        <Modal show={show} centered onHide={() => setShow(false)}>
            <Form onSubmit={handleSubmit(submit)}>
                <Modal.Header closeButton>
                    <HeaderText>{modalTitle}</HeaderText>
                </Modal.Header>
                <Modal.Body>
                    <Input
                        type="text"
                        label={mode === 'invite' ? 'Staff Name' : 'Name'}
                        {...register("name", { required: "Name is required" })}
                        placeholder={mode === 'invite' ? 'Staff Member Name' : 'Staff Member Name'}
                        error={errors.name}
                    />

                    <Input
                        type="email"
                        label={mode === 'invite' ? 'Email' : 'Email Address'}
                        {...register("emailAddress", {
                            required: "Email is required",
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address"
                            }
                        })}
                        placeholder={mode === 'invite' ? 'staff@example.com' : 'staff@example.com'}
                        error={errors.emailAddress}
                    />

                    <Input
                        type="text"
                        label={mode === 'invite' ? 'Phone Number (optional)' : 'Phone Number'}
                        {...register("phoneNumber", {
                            pattern: {
                                value: /^\d{3}-\d{3}-\d{4}$/,
                                message: "Phone number must be in format ###-###-####"
                            }
                        })}
                        placeholder="555-555-5555"
                        error={errors.phoneNumber}
                    />

                    <Select
                        label="Role"
                        {...register("selectedRole", { required: "Role is required" })}
                        onChange={selectedRoleChange}
                        error={errors.selectedRole}
                    >
                        {roles.map(role => (
                            <option key={role.identity}>
                                {role.text}
                            </option>
                        ))}
                    </Select>

                    {(facilities && showFacilities) && (
                        <div>
                            <Form.Label className="mb-1">Facilities</Form.Label>
                            <div>
                            {facilities.length > 1 && (
                                <Button 
                                    type="button" 
                                    size="sm" 
                                    variant="outline-primary" 
                                    className="mb-2"
                                    onClick={selectAll}
                                >
                                    Select All
                                </Button>
                            )}
                            </div>
                            <div className="mb-2">
                                <Multiselect 
                                    onChange={selectedFacilityChange}
                                    value={selectedValues}
                                    isMulti 
                                    options={facilities} 
                                />
                            </div>
                           
                        </div>
                    )}

                    <Input
                        as="textarea"
                        type="text"
                        label={mode === 'invite' ? 'Note' : 'Notes'}
                        {...register("note")}
                        placeholder={mode === 'invite' ? 'Staff member notes' : 'Staff member notes'}
                        rows={3}
                        error={errors.note}
                    />
                </Modal.Body>
                <Modal.Footer className="d-flex">
                    <Button className="flex-grow-1" onClick={() => setShow(false)}>Cancel</Button>
                    <Button 
                        className={`flex-grow-1 btn-main`} 
                        type="submit"
                    >
                        {mode === 'invite' ? 'Invite' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
} 